<template>
  <div class="about-container">
    <div class="about-header">
      <h1>About Us</h1>
      <p>Your Trusted Travel Partner Since 2010</p>
    </div>

    <section class="about-content">
      <div class="mission">
        <h2>About Us</h2>
        <p>Welcome to Real Yunnan! We are your specialists for inbound, tailor-made travel experiences in China's stunning Yunnan province. We design personalized tours for foreign visitors, handling everything from hotel bookings and expert local guides to car rentals and comprehensive packages. Our passion is crafting unique journeys that let you authentically discover the breathtaking landscapes and rich ethnic cultures of Yunnan, exactly your way. Experience the real Yunnan with us.
        </p>
      </div>
      <div class="why-choose-us">
        <h2>What Sets Us Apart</h2>
        <div class="features-table">
          <div class="feature-row">
          <div class="feature-left">
              <font-awesome-icon icon="house-chimney-window" />
              <h3>Tailor-Made Yunnan Tours</h3>
            </div>
            <div class="feature-right">
              <p>We craft personalized journeys, meticulously designed to match your unique interests and travel style for an unforgettable Yunnan adventure.</p>
            </div>
          </div>
          <div class="feature-row">
            <div class="feature-left">
              <font-awesome-icon icon="bed" />
              <h3>Authentic Cultural & Natural Immersion</h3>
            </div>
            <div class="feature-right">
              <p>Experience Yunnan's stunning landscapes and vibrant ethnic minority cultures deeply, creating genuine connections and lasting memories.</p>
            </div>
          </div>
          <div class="feature-row">
            <div class="feature-left">
              <font-awesome-icon icon="user-tie" />
              <h3>All-Inclusive, Hassle-Free Service</h3>
            </div>
            <div class="feature-right">
              <p>From hotels and expert guides to transport and curated packages, we seamlessly manage all your Yunnan travel needs.</p>
            </div>
          </div>
          <div class="feature-row">
            <div class="feature-left">
              <font-awesome-icon icon="tree" />
              <h3>In-Depth Yunnan Exploration</h3>
            </div>
            <div class="feature-right">
              <p>Discover the real, hidden gems of Yunnan beyond the typical routes, offering profound insights and authentic local encounters.</p>
            </div>
          </div>
          <div class="feature-row">
            <div class="feature-left">
              <font-awesome-icon icon="phone" />
              <h3>Seamless Support</h3>
            </div>
            <div class="feature-right">
              <p>24 / 7 bilingual assistance from our Kunming headquarters before, during, and after your trip.</p>
            </div>
          </div>
        </div>
      </div>

      <TestimonialsSection :greenBackground="false" />
      <TravelerMemories :altBackground="false" />

      <div class="mission">
        <h2>Our Mission</h2>
        <p>Real Yunnan, born from a passion for sharing the breathtaking beauty and vibrant heart of Yunnan with the world. We are a specialist inbound travel agency dedicated to crafting unforgettable, tailor-made journeys for international visitors. Our dream is simple: to help you experience the real Yunnan.</p>
        <p>We believe that travel should be personal and profound. That's why we focus on customized tours, meticulously designing every detail – from hotel bookings and expert local guides to comfortable transportation and unique package experiences. Our goal is to take you beyond the surface, immersing you in Yunnan's stunning natural landscapes, the fascinating traditions of its diverse ethnic minorities, and the kind of in-depth exploration that creates lifelong memories. With Real Yunnan, discover the authentic soul of this incredible land.</p>
      </div>

      <div class="team-section">
        <h2>Meet Our Team</h2>
        <div class="team-carousel-wrapper">
            <button
                class="nav-button prev"
                @click="scrollTeam('prev')"
                :disabled="isAtStart">
                <font-awesome-icon icon="chevron-left" />
            </button>
            <div class="team-carousel" ref="teamCarouselRef" @scroll="handleScroll">
              <div class="team-container">
                <div v-for="(member, index) in teamMembers"
                     :key="member.name"
                     class="team-member"
                     :class="{ 'active': index === centerIndex }">
                  <img :src="member.image" :alt="member.name" draggable="false">
                  <h3>{{ member.name }}</h3>
                  <p>{{ member.position }}</p>
                </div>
              </div>
            </div>
             <button
                class="nav-button next"
                @click="scrollTeam('next')"
                :disabled="isAtEnd">
                <font-awesome-icon icon="chevron-right" />
            </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed, getCurrentInstance } from 'vue';
import TestimonialsSection from '../components/TestimonialsSection.vue';
import TravelerMemories from '../components/TravelerMemories.vue';
import { teamMembers } from '../data/memberData.js';

const teamCarouselRef = ref(null);
const centerIndex = ref(2);
const itemWidth = ref(200);
const itemWidthWithGap = ref(224);
const debounceTimer = ref(null);
const currentScrollLeft = ref(0);
const { proxy } = getCurrentInstance();

const calculateWidths = async () => {
    await nextTick();
    const container = teamCarouselRef.value;
    if (!container || !container.firstElementChild || container.firstElementChild.children.length < 1) {
        console.warn("Cannot calculate widths yet.");
        itemWidth.value = 200;
        itemWidthWithGap.value = 200 + 24;
        return;
    }
    const firstItem = container.firstElementChild.children[0];
    itemWidth.value = firstItem.offsetWidth;

    if (container.firstElementChild.children.length > 1) {
        const secondItem = container.firstElementChild.children[1];
        if (secondItem.offsetLeft > firstItem.offsetLeft + itemWidth.value) {
             itemWidthWithGap.value = secondItem.offsetLeft - firstItem.offsetLeft;
        } else {
             const gap = parseFloat(getComputedStyle(container.firstElementChild).gap) || 24;
             itemWidthWithGap.value = itemWidth.value + gap;
        }
    } else {
        const gap = parseFloat(getComputedStyle(container.firstElementChild).gap) || 24;
        itemWidthWithGap.value = itemWidth.value + gap;
    }
     console.log(`Calculated itemWidth: ${itemWidth.value}, itemWidthWithGap: ${itemWidthWithGap.value}`);
     currentScrollLeft.value = container.scrollLeft;
     updateCenterIndex();
};

const isAtStart = computed(() => currentScrollLeft.value <= 0);
const isAtEnd = computed(() => {
    const container = teamCarouselRef.value;
    if (!container) return true;
    return currentScrollLeft.value >= container.scrollWidth - container.clientWidth - 1;
});

const handleScroll = () => {
    const container = teamCarouselRef.value;
    if (container) {
        currentScrollLeft.value = container.scrollLeft;
    }
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }
  debounceTimer.value = setTimeout(updateCenterIndex, 50);
};

const updateCenterIndex = () => {
    const container = teamCarouselRef.value;
    if (!container || itemWidthWithGap.value <= 0) return;

    const scrollLeft = container.scrollLeft;
    const containerWidth = container.offsetWidth;
    const scrollCenter = scrollLeft + containerWidth / 2;
    const calculatedIndex = Math.round((scrollCenter - itemWidth.value / 2) / itemWidthWithGap.value);
    const newCenterIndex = Math.max(0, Math.min(calculatedIndex, teamMembers.length - 1));

    if (newCenterIndex !== centerIndex.value) {
        centerIndex.value = newCenterIndex;
    }
};

const scrollTeam = (direction) => {
    const container = teamCarouselRef.value;
    if (!container || itemWidthWithGap.value <= 0) return;

    const scrollAmount = direction === 'next' ? itemWidthWithGap.value : -itemWidthWithGap.value;

    container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
    });
};

onMounted(() => {
  calculateWidths();
  window.addEventListener('resize', calculateWidths);
  proxy.$meta.update({
    title: 'About Real Yunnan - Meet Your Local Yunnan Travel Experts',
    description: 'Learn about our team, our mission, and why we are the trusted Yunnan travel agency for your journey. Discover our passion for authentic, tailor-made Yunnan experiences.',
    image: '/images/logo.png',
    canonicalUrl: 'https://realyunnan.com/about/',
    ogTitle: 'About Real Yunnan - Meet Your Local Yunnan Travel Experts',
    ogDescription: 'Learn about our team, our mission, and why we are the trusted Yunnan travel agency for your journey. Discover our passion for authentic, tailor-made Yunnan experiences.',
    ogImage: '/images/logo.png',
    ogUrl: 'https://realyunnan.com/about/'
  });
});

import { onUnmounted } from 'vue';
onUnmounted(() => {
  window.removeEventListener('resize', calculateWidths);
   if (debounceTimer.value) {
       clearTimeout(debounceTimer.value);
   }
});
</script>

<style scoped>
.about-container {
  width: 100%;
  min-height: calc(100vh - 70px);
  background-color: var(--color-background-soft);
}

.about-header {
  text-align: center;
  padding: 8rem 1rem;
  background: url('/images/about/about-banner.jpg');
  background-size: cover;
  background-position: center 60%;
  color: white;
  margin-bottom: 3rem;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.about-header h1 {
  font-size: 2.8rem;
  margin-bottom: 1.5rem;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
}

.about-header p {
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
}

.about-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 3rem;
}

.mission {
  background: var(--color-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  margin-bottom: 3rem;
}

.mission h2 {
  font-size: 2rem;
}

.mission p {
  padding: 0.5rem 0;
  font-size: 1.2rem;
}

.mission-list {
  list-style-type: none;
  padding: 0.5rem 0 1rem 0;
  margin: 0;
  font-size: 1rem;
}

.mission-list li {
  position: relative;
  padding: 0.75rem 0;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.mission-list li:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.mission-list li strong {
  color: #5a915c;
  font-weight: 700;
  font-size: 1rem;
}

.tour-main h2 {
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: var(--color-heading);
  position: relative;
}

.features-table {
  margin: 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.feature-row {
  display: flex;
  align-items: center;
  background: var(--color-background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transition: transform 0.3s ease;
}

.feature-row:hover {
  transform: translateY(-5px);
}

.feature-left {
  flex: 0 0 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-right: 1.5rem;
  border-right: 1px solid rgba(0,0,0,0.08);
}

.feature-left .svg-inline--fa {
  font-size: 2.5rem;
  color: #5a915c;
  margin-bottom: 0.75rem;
}

.feature-left h3 {
  margin: 0;
  text-align: center;
  font-size: 1.1rem;
  color: var(--color-heading);
}

.feature-right {
  flex: 1;
  padding-left: 1.5rem;
}

.feature-right p {
  margin: 0;
  font-size: 1rem;
}

.team-section {
  background: var(--color-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--card-shadow);
  margin-top: 3rem;
}

.team-section h2 {
  color: var(--color-heading);
  text-align: center;
  margin-bottom: 2rem;
}

.team-carousel-wrapper {
    position: relative;
    max-width: 1180px;
    margin: 0 auto;
}

.team-carousel {
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 2rem 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
}

.team-carousel::-webkit-scrollbar {
  display: none;
}

.team-container {
  display: flex;
  width: max-content;
  gap: 1.5rem;
}

.team-member {
  flex: 0 0 200px;
  text-align: center;
  transition: transform 0.4s ease, opacity 0.4s ease;
  padding: 0.5rem;
  opacity: 0.65;
  scroll-snap-align: center;
}

.team-member img {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
  transform: scale(1);
  margin-bottom: 1rem;
  box-shadow: 0 4px 8px rgba(0,0,0,0.08);
}

.team-member.active {
  opacity: 1;
}

.team-member.active img {
  transform: scale(1.4);
  box-shadow: 0 10px 20px rgba(0,0,0,0.18);
}

.team-member h3 {
  margin: 0.5rem 0;
  font-size: 1.1rem;
  color: var(--color-heading);
}

.team-member p {
  font-size: 0.9rem;
  color: var(--color-text-soft);
}

.nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.8);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    transition: background-color 0.3s ease, opacity 0.3s ease;
}

.nav-button.prev {
    left: -20px;
}

.nav-button.next {
    right: -20px;
}

.nav-button:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 1);
}

.nav-button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.nav-button .svg-inline--fa {
    font-size: 1rem;
    color: var(--color-heading);
}

@media (max-width: 1200px) {
  .team-carousel {
    max-width: 1096px;
  }
}

@media (max-width: 992px) {
   .team-carousel {
    max-width: 872px;
  }
}

@media (max-width: 768px) {

  .mission {
    margin-bottom: 2rem;
  }

  .mission p {
    padding: 0.5rem 0;
    font-size: 1rem;
  }

  .team-carousel {
    max-width: 648px;
  }
  .about-header {
    padding: 3rem 1rem;
    min-height: 300px;
    height: auto;
  }
  
  .about-header h1 {
    font-size: 2.2rem;
  }
  .feature-row {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem;
  }
  
  .feature-left {
    flex: 0 0 auto;
    border-right: none;
    border-bottom: 1px solid rgba(0,0,0,0.08);
    padding-right: 0;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
    width: 100%;
  }
  
  .feature-right {
    padding-left: 0;
    width: 100%;
  }
}

@media (max-width: 576px) {
  .team-carousel {
    max-width: 424px;
  }
  .nav-button {
     width: 35px;
     height: 35px;
   }
   .nav-button.prev { left: -15px; }
   .nav-button.next { right: -15px; }
}

@media (max-width: 480px) {
  .about-header {
    padding: 2rem 1rem;
    min-height: 200px;
  }
  
  .about-header h1 {
    font-size: 1.8rem;
  }
}

</style> 